import { useCallback, useContext, useMemo } from 'preact/hooks';

import { emptyCountry, AppContext } from '../context/AppContext';
import WorldMap from './world-map/WorldMap';

const countryDefaults = {
	fill: 'navy',
	stroke: 'white',
	className: 'tcc-map__country',
};

/**
 * Determine highlighted class for country.
 *
 * @param {Array}  countryRegions   - country region array
 * @param {number} filteredRegion   - active region state value
 * @param {Array}  countryPrograms  - country program array
 * @param {Array}  filteredPrograms - active program state value
 * @param {boolean}  legacyPrograms   - legacy program state value
 * @return {string} - is-highlighted, is-not-highlighted
 */
function determineHighlighted( countryRegions, filteredRegion, countryPrograms, filteredPrograms, legacyPrograms ) {
	// Determine if filters are applied.
	const hasFilteredRegion = ( filteredRegion?.slug !== '' );
	const hasFilteredPrograms = ( filteredPrograms?.length > 0 );
	
	// Determine if the country contains only legacy programs.
	const onlyLegacyPrograms = countryPrograms.every( ( program ) => program.includes( 'legacy' ) );

	// Check if the country has a matching region.
	const hasMatchingRegion = countryRegions.includes( filteredRegion.slug );

	const show = 'is-highlighted';
	const hide = 'is-not-highlighted';

	// Highligting logic for legacy programs.
	if ( ! legacyPrograms ) {
		// Initial state: hide countries with only legacy programs.
		if ( ! hasFilteredRegion && ! hasFilteredPrograms && onlyLegacyPrograms ) {
			return hide;
		}

		// If region is filtered, hide countries with only legacy programs.
		if ( hasMatchingRegion && ! hasFilteredPrograms && onlyLegacyPrograms ) {
			return hide;
		}
	}

	// Create legacy programs array to mirror filtered programs.
	const filteredLegacyPrograms = filteredPrograms.map( ( program ) => program + '-legacy' );

	// Check if the country has a matching program.
	// If legacyPrograms is true, check both filteredPrograms and filteredLegacyPrograms.
	const programsToCheck = legacyPrograms ? 
		[...filteredPrograms, ...filteredLegacyPrograms] : 
		filteredPrograms;
	const hasMatchingProgram = countryPrograms.some(program => programsToCheck.includes(program));

	// Standard highlighting locic.
	// Enforces AND logic between region and program filters.
	switch ( true ) {
		// Initial state: highlight all if no filters are applied.
		case ! hasFilteredRegion && ! hasFilteredPrograms:
			return show;
		// Filtered Regions.
		case hasMatchingRegion && ! hasFilteredPrograms:
			return show;
		// Filtered Programs.
		case hasMatchingProgram && ! hasFilteredRegion:
			return show;
		// Filtered Regions and Programs.
		case hasMatchingRegion && hasMatchingProgram:
			return show;
		// No match.
		default:
			return hide;
	}
}

/**
 * Using the app context, render the WorldMap component with state and logic applied to countries.
 *
 * @param {Object} props                - component props
 * @param {*}      props.wpCountryPosts
 * @return {*} jsx
 */
export default function MapWithContext( { wpCountryPosts } ) {
	const context = useContext( AppContext );
	const { filterContext } = context;
	const [ state, dispatch ] = filterContext;
	const { activeCountry, region, programs, legacyPrograms } = state;

	// Create an object of country settings keyed by ISO number.
	const countrySettings = useMemo(
		() => wpCountryPosts.reduce( ( acc, country ) => {
			// Skip if no ISO number.
			if ( ! country.iso_num_id ) {
				return acc;
			}

			const isActive = activeCountry?.post_id === country.post_id;
			const hasHealth = country.programs.includes( 'health' ) || country.programs.includes( 'health-legacy' );
			const hasPeace = country.programs.includes( 'peace' ) || country.programs.includes( 'peace-legacy' );

			// Build class string.
			const countryClasses = [
				'tcc-map__country has-post',
				isActive && 'is-selected',
				hasHealth && 'has-health',
				hasPeace && 'has-peace',
				determineHighlighted( country.regions, region, country.programs, programs, legacyPrograms ),
			].filter( Boolean ).join( ' ' );

			const settings = {
				data: {
					post_id: country.post_id,
					name: country.name,
					regions: country.regions,
					programs: country.programs,
				},
				pathAttributes: {
					fill: 'blue',
					stroke: 'red',
					className: countryClasses,
				},
			};

			// Look for associated ISO number.
			// If it exists, add the settings to the paired ISO.
			const associatedIso = country.associated_iso_num_id;
			if ( country.associated_iso_num_id ) {

				// Skip if the ISO is already in the settings.
				if ( acc[country.associated_iso_num_id] ) {
					return;
				}
				// Add the settings to the paired ISO.
				acc[country.associated_iso_num_id] = settings;
			}

			acc[ country.iso_num_id ] = settings;

			return acc;
		}, {} ), [ wpCountryPosts, region, programs, activeCountry, legacyPrograms ]
	);

	const handleZoom = useCallback(
		( type ) => {
			if ( type === 'zoomReset' && activeCountry?.post_id ) {
				dispatch( { type: 'SET_ACTIVE_COUNTRY', payload: emptyCountry } );
			}
		}, [ dispatch, activeCountry ]
	);

	const focusedCountryIso = useMemo(
		() => {
			const country = wpCountryPosts.find( ( post ) => post.post_id === activeCountry?.post_id );
			return country?.iso_num_id ?? '';
		}, [ activeCountry, wpCountryPosts ]
	);

	const handleCountryClick = useCallback(
		( obj ) => {
			const { data } = obj;

			// Return early if no data and stop zoom.
			if ( ! data ) {
				return false;
			}

			// Return if not in region.
			if ( region.slug && ! data.regions.includes( region.slug ) ) {
				return false;
			}

			const country = {
				name: data?.name ?? '',
				post_id: data?.post_id ?? 0,
			};

			dispatch( { type: 'SET_ACTIVE_COUNTRY', payload: country } );
		}, [ dispatch, region ]
	);

	return <WorldMap
		countrySettings={ countrySettings }
		onZoomAction={ handleZoom }
		onCountryClick={ handleCountryClick }
		countryDefaultAttributes={ countryDefaults }
		focusedCountry={ focusedCountryIso }
	/>;
}
